import {
  EditOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  ASelect,
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { MenuProps, Tag } from 'antd';
import { IDocument } from '../../shared/interfaces';
import api from '../../services/api';
import { formatDate } from '../../shared/utils/format-date';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Documents: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>('');
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const isPrescriptionRoute = location.pathname.startsWith('/prescription');
  const kind =
    isPrescriptionRoute || searchParams.get('kind') === 'PRESCRIPTION'
      ? 'PRESCRIPTION'
      : 'GENERAL';
  const isPrescription = kind === 'PRESCRIPTION';

  const getListRoute = (value?: string) => {
    const basePath = value === 'PRESCRIPTION' ? '/prescriptions' : '/documents';
    return client_id ? `${basePath}/${client_id}` : basePath;
  };

  const getCreateRoute = (value?: string) =>
    value === 'PRESCRIPTION' ? '/prescription/new' : '/document/new';

  const getEditRoute = (id?: string, value?: string) =>
    value === 'PRESCRIPTION' ? `/prescription/${id}` : `/document/${id}`;

  const getPrintRoute = (id?: string, value?: string) =>
    value === 'PRESCRIPTION'
      ? `/prescription/${id}/print`
      : `/document/${id}/print`;

  const listClientDocuments = useCallback(async () => {
    setLoading(true);
    setDocuments([]);
    try {
      const response = await api.post('/clients-documents/list', {
        client_id: client_id,
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
        kind,
      });
      setPages(response.data.count);
      setDocuments(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: `Erro ao listar ${
          isPrescription ? 'receituários' : 'documentos'
        }`,
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, client_id, kind, isPrescription]);

  const handleKindChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value === 'PRESCRIPTION') {
      nextParams.set('kind', 'PRESCRIPTION');
    } else {
      nextParams.delete('kind');
    }

    setPage(1);
    setSearchParams(nextParams);
    navigate(getListRoute(value));
  };

  const renderMenuOptions = () => {
    const options: MenuProps['items'] = [];
    options.push({
      label: 'Atualizar',
      key: '1',
      icon: <RedoOutlined />,
      onClick: () => listClientDocuments(),
    });
    if (!client_id) {
      if (permissionFind([PermissionActions.documentsCreate])) {
        options.push({
          label: isPrescription ? 'Cadastrar receituário' : 'Cadastrar',
          key: '2',
          icon: <PlusCircleOutlined />,
          onClick: () => navigate(getCreateRoute(kind)),
        });
      }
    }
    return options;
  };

  useEffect(() => {
    listClientDocuments();
  }, [page, search, limit, listClientDocuments]);

  return (
    <PageLayout>
      <PageIntro
        badge={isPrescription ? 'Receituários' : 'Documentos'}
        icon={<FileTextOutlined />}
        title={isPrescription ? 'Receituários eletrônicos' : 'Documentos'}
        description={
          isPrescription
            ? 'Gerencie prescrições digitais, acompanhe assinatura do profissional e imprima o receituário rapidamente.'
            : 'Gerencie os documentos emitidos, consulte assinaturas e acesse rapidamente as opções de edição e impressão.'
        }
      />
      <MenuOptions
        menu_items={renderMenuOptions()}
        showButtonBack={!!client_id}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <ASelect
          value={kind}
          onChange={handleKindChange}
          style={{ width: 220 }}
          options={[
            { label: 'Documentos', value: 'GENERAL' },
            { label: 'Receituários', value: 'PRESCRIPTION' },
          ]}
        />
      </div>
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              dataIndex: 'code',
            },
            {
              title: 'Data',
              dataIndex: 'created_at',
              width: 110,
              render: (value: string) => <>{formatDate(value)}</>,
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
            },
            {
              title: 'Profissional',
              dataIndex: 'professional_name',
            },
            {
              title: 'Titulo',
              dataIndex: 'title_document',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Assinatura',
              width: 120,
              render: (cell: IDocument) => {
                const isSigned = Boolean(
                  cell.signature_validation_code || cell.signed_at,
                );

                return (
                  <Tag color={isSigned ? 'success' : 'warning'}>
                    {isSigned ? 'Assinado' : 'Não assinado'}
                  </Tag>
                );
              },
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IDocument) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.documentsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(getEditRoute(cell.id, cell.kind || kind));
                          },
                        }
                      : null,
                    {
                      label: 'Imprimir',
                      key: '2',
                      icon: <PrinterOutlined />,
                      onClick: () => {
                        navigate(getPrintRoute(cell.id, cell.kind || kind));
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={documents}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default Documents;
